/**
 * deviceFingerprint.js
 *
 * Genera una huella digital única y estable por dispositivo/navegador.
 * Usa múltiples señales de hardware/software para crear un hash SHA-256:
 *
 *   - user agent, plataforma, lenguaje, timezone
 *   - resolución de pantalla, color depth
 *   - hardware concurrency, device memory
 *   - canvas fingerprint (subpixel rendering distinto por GPU)
 *   - WebGL vendor + renderer (identifica la GPU)
 *
 * IMPORTANTE: NO es la dirección MAC física — los navegadores web NO
 * exponen la MAC address por razones de seguridad (sandbox de Chrome,
 * Safari, Firefox). Este fingerprint es la mejor alternativa disponible
 * en la web, usada por Google, Netflix, bancos, etc. Es lo suficientemente
 * único para identificar el dispositivo aunque el usuario:
 *   - Cambie de red WiFi
 *   - Use VPN o proxy
 *   - Use modo incógnito (mismo hash porque el hardware es el mismo)
 *   - Cambie de IP (celular, casa, oficina)
 *
 * Cambia solo si el usuario:
 *   - Formatea el equipo / reinstala SO
 *   - Actualiza el navegador drásticamente (nueva versión mayor)
 *   - Cambia de navegador (Chrome → Safari)
 */

const FP_CACHE_KEY = "ca_device_fp_v1";

const stableHash = async (str) => {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const canvasFP = () => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 220;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Cwm fjordbank ✦", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Cwm fjordbank ✦", 4, 17);
    return canvas.toDataURL();
  } catch {
    return "";
  }
};

const webglFP = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "";
    const vendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "";
    const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "";
    return `${vendor}|${renderer}`;
  } catch {
    return "";
  }
};

/**
 * Public API — retorna el fingerprint (hex SHA-256 de 64 chars).
 * Cachea el resultado en localStorage para evitar recalcular.
 */
export const getDeviceFingerprint = async () => {
  if (typeof window === "undefined") return "";
  try {
    const cached = localStorage.getItem(FP_CACHE_KEY);
    if (cached && cached.length === 64) return cached;
  } catch {
    /* localStorage might be disabled */
  }

  const parts = [];
  try {
    parts.push(navigator.userAgent || "");
    parts.push(navigator.language || "");
    parts.push((navigator.languages || []).join(","));
    parts.push(navigator.platform || "");
    parts.push(String(navigator.hardwareConcurrency || 0));
    parts.push(String(navigator.deviceMemory || 0));
    parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || "");
    parts.push(`${screen.width}x${screen.height}`);
    parts.push(`d${screen.colorDepth}p${window.devicePixelRatio || 1}`);
    parts.push(String(new Date().getTimezoneOffset()));
    parts.push(canvasFP());
    parts.push(webglFP());
  } catch {
    /* if any collection throws, we still hash what we have */
  }

  const raw = parts.join("|");
  const hex = await stableHash(raw);
  try {
    localStorage.setItem(FP_CACHE_KEY, hex);
  } catch {
    /* ignore */
  }
  return hex;
};

/**
 * Retorna metadata legible (útil para debugging / admin panel).
 */
export const getDeviceInfo = () => ({
  user_agent: typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  language: typeof navigator !== "undefined" ? navigator.language || "" : "",
  platform: typeof navigator !== "undefined" ? navigator.platform || "" : "",
  timezone:
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "" : "",
  screen:
    typeof screen !== "undefined" ? `${screen.width}x${screen.height}@${screen.colorDepth}` : "",
  cores: typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 0 : 0,
  memory_gb: typeof navigator !== "undefined" ? navigator.deviceMemory || 0 : 0,
});
